"use client";

import { createLogAdapter } from "@/lib/logger/console-adapter";

export type OkxSignType = "ecdsa" | "bip322-simple";

export interface OkxConnectAccount {
	address: string;
	publicKey?: string;
}

export interface OkxConnectClient {
	getAccount: () => Promise<OkxConnectAccount>;
	signMessage: (message: string, type?: OkxSignType) => Promise<string>;
}

const uiLog = createLogAdapter("lib/services/okx-connect");

const OKX_BTC_CHAIN = "btc:mainnet";

let okxUiPromise: Promise<any> | null = null;

const resolveLanguage = (lang?: string): "en_US" | "zh_CN" => {
	const source = lang || (typeof navigator !== "undefined" ? navigator.language : "en");
	return source.toLowerCase().startsWith("zh") ? "zh_CN" : "en_US";
};

const getDappIconUrl = (): string => {
	if (typeof window === "undefined") return "";
	return `${window.location.origin}/images/baby-odin-logo.png`;
};

const getSessionRedirectUrl = (): string | undefined => {
	if (typeof window === "undefined") {
		return undefined;
	}

	return window.location.href;
};

const initOkxUi = async (language?: string): Promise<any> => {
	if (okxUiPromise) return okxUiPromise;

	okxUiPromise = (async () => {
		if (typeof window === "undefined") {
			throw new Error("OKX Connect UI only available in browser");
		}

		const okxUiModule = await import("@okxconnect/ui");
		const { OKXUniversalConnectUI, THEME } = okxUiModule;

		const lang = resolveLanguage(language);
		const dappMetaData = {
			name: "BabyOdin",
			icon: getDappIconUrl(),
		};

		const ui = await OKXUniversalConnectUI.init({
			dappMetaData,
			actionsConfiguration: {
				returnStrategy: "none",
			},
			uiPreferences: {
				theme: THEME.LIGHT,
			},
			language: lang,
		});

		return ui;
	})();

	return okxUiPromise;
};

const buildConnectParams = () => ({
	namespaces: {
		btc: {
			chains: [OKX_BTC_CHAIN],
			defaultChain: OKX_BTC_CHAIN,
		},
	},
	sessionConfig: {
		redirect: getSessionRedirectUrl(),
	},
});

const ensureConnected = async (ui: any): Promise<void> => {
	try {
		if (typeof ui?.connected === "function" && ui.connected()) {
			return;
		}
	} catch (error) {
		uiLog.warn("Failed to check OKX connection status", error);
	}

	const connectParams = buildConnectParams();

	if (typeof ui?.connect === "function") {
		await ui.connect(connectParams);
		return;
	}

	if (typeof ui?.openModal === "function") {
		await ui.openModal(connectParams);
		return;
	}

	throw new Error("OKX Connect UI does not support connect methods");
};

const getBtcProvider = async (ui: any): Promise<any> => {
	const { OKXBtcProvider } = await import("@okxconnect/universal-provider");
	return new OKXBtcProvider(ui);
};

const normalizeAccount = (account: any): OkxConnectAccount => {
	if (!account) {
		throw new Error("OKX Connect account not found");
	}

	if (typeof account === "string") {
		return { address: account };
	}

	return {
		address: account.address || account.addr || account[0] || "",
		publicKey: account.publicKey,
	};
};

export async function getOkxConnectClient(options?: {
	language?: string;
}): Promise<OkxConnectClient> {
	const ui = await initOkxUi(options?.language);
	await ensureConnected(ui);
	const btcProvider = await getBtcProvider(ui);

	return {
		getAccount: async () => {
			if (typeof btcProvider.getAccount === "function") {
				const account = await btcProvider.getAccount(OKX_BTC_CHAIN);
				const normalized = normalizeAccount(account);
				if (!normalized.address) {
					throw new Error("OKX Connect account missing address");
				}
				return normalized;
			}

			if (typeof btcProvider.getAccounts === "function") {
				const accounts = await btcProvider.getAccounts(OKX_BTC_CHAIN);
				if (Array.isArray(accounts) && accounts.length > 0) {
					const normalized = normalizeAccount(accounts[0]);
					if (!normalized.address) {
						throw new Error("OKX Connect account missing address");
					}
					return normalized;
				}
			}

			throw new Error("OKX Connect account not available");
		},
		signMessage: async (message: string, type: OkxSignType = "ecdsa") => {
			return btcProvider.signMessage(OKX_BTC_CHAIN, message, type);
		},
	};
}

export function prewarmOkxConnect(options?: { language?: string }): void {
	void initOkxUi(options?.language)
		.then(async () => {
			await import("@okxconnect/universal-provider");
		})
		.catch((error) => {
			uiLog.warn("Failed to prewarm OKX Connect", error);
		});
}
